/**
 * 专业尽职调查API端点定义
 * 基于 PROFESSIONAL_DUE_DILIGENCE_DESIGN.md 设计规范
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQuery } from './baseApi';
import type {
  // 响应类型
  DDProjectResponse,
  SaveDraftResponse,
  SubmitReportResponse,
  UploadResponse,
  DDDocumentListResponse,

  // 请求类型
  SaveLegalDDRequest,
  SubmitLegalDDRequest,
  SaveFinancialDDRequest,
  SubmitFinancialDDRequest,
  SaveGreenCertDDRequest,
  SubmitGreenCertDDRequest,
  SaveESGDDRequest,
  SubmitESGDDRequest,
  UploadDDDocumentRequest,
  GetDDDocumentsRequest,

  // 数据类型
  ProfessionalDueDiligenceData,
  LegalDueDiligenceData,
  FinancialDueDiligenceData,
  GreenCertificationDDData,
  ESGDueDiligenceData,
} from '../../pages/project-management/workflow-tasks/professional-due-diligence/shared/types/professionalDD';

export const professionalDueDiligenceApi = createApi({
  reducerPath: 'professionalDueDiligenceApi',
  baseQuery: createBaseQuery(),
  tagTypes: ['DDReport', 'DDProgress', 'DDDocument'],
  endpoints: builder => ({
    // ============================================================================
    // 通用查询端点
    // ============================================================================

    /**
     * 获取专业尽调项目详情
     */
    getDDProjectDetail: builder.query<DDProjectResponse, string>({
      query: projectId => `/professional-dd/${projectId}`,
      providesTags: (result, error, projectId) => [
        { type: 'DDReport', id: projectId },
        { type: 'DDProgress', id: projectId },
      ],
    }),

    /**
     * 获取专业尽调进度概览
     */
    getDDProgress: builder.query<
      {
        success: boolean;
        data: {
          legalProgress: number;
          financialProgress: number;
          greenCertProgress: number;
          esgProgress: number;
          overallProgress: number;
        };
      },
      string
    >({
      query: projectId => `/professional-dd/${projectId}/progress`,
      providesTags: (result, error, projectId) => [
        { type: 'DDProgress', id: projectId },
      ],
    }),

    // ============================================================================
    // 法律尽职调查端点
    // ============================================================================

    /**
     * 保存法律尽调草稿
     */
    saveLegalDDDraft: builder.mutation<SaveDraftResponse, SaveLegalDDRequest>({
      query: ({ projectId, data }) => ({
        url: `/professional-dd/${projectId}/legal/draft`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'DDProgress', id: projectId },
      ],
    }),

    /**
     * 提交法律尽调报告
     */
    submitLegalDDReport: builder.mutation<
      SubmitReportResponse,
      SubmitLegalDDRequest
    >({
      query: ({ projectId, data }) => ({
        url: `/professional-dd/${projectId}/legal/submit`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'DDReport', id: projectId },
        { type: 'DDProgress', id: projectId },
      ],
    }),

    /**
     * 获取法律尽调数据
     */
    getLegalDDData: builder.query<
      {
        success: boolean;
        data: LegalDueDiligenceData;
      },
      string
    >({
      query: projectId => `/professional-dd/${projectId}/legal`,
      providesTags: (result, error, projectId) => [
        { type: 'DDReport', id: `legal-${projectId}` },
      ],
    }),

    // ============================================================================
    // 财务尽职调查端点
    // ============================================================================

    /**
     * 保存财务尽调草稿
     */
    saveFinancialDDDraft: builder.mutation<
      SaveDraftResponse,
      SaveFinancialDDRequest
    >({
      query: ({ projectId, data }) => ({
        url: `/professional-dd/${projectId}/financial/draft`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'DDProgress', id: projectId },
      ],
    }),

    /**
     * 提交财务尽调报告
     */
    submitFinancialDDReport: builder.mutation<
      SubmitReportResponse,
      SubmitFinancialDDRequest
    >({
      query: ({ projectId, data }) => ({
        url: `/professional-dd/${projectId}/financial/submit`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'DDReport', id: projectId },
        { type: 'DDProgress', id: projectId },
      ],
    }),

    /**
     * 获取财务尽调数据
     */
    getFinancialDDData: builder.query<
      {
        success: boolean;
        data: FinancialDueDiligenceData;
      },
      string
    >({
      query: projectId => `/professional-dd/${projectId}/financial`,
      providesTags: (result, error, projectId) => [
        { type: 'DDReport', id: `financial-${projectId}` },
      ],
    }),

    // ============================================================================
    // 绿色认证尽调端点
    // ============================================================================

    /**
     * 保存绿色认证尽调草稿
     */
    saveGreenCertificationDDDraft: builder.mutation<
      SaveDraftResponse,
      SaveGreenCertDDRequest
    >({
      query: ({ projectId, data }) => ({
        url: `/professional-dd/${projectId}/green-cert/draft`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'DDProgress', id: projectId },
      ],
    }),

    /**
     * 提交绿色认证尽调报告
     */
    submitGreenCertificationDDReport: builder.mutation<
      SubmitReportResponse,
      SubmitGreenCertDDRequest
    >({
      query: ({ projectId, data }) => ({
        url: `/professional-dd/${projectId}/green-cert/submit`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'DDReport', id: projectId },
        { type: 'DDProgress', id: projectId },
      ],
    }),

    /**
     * 获取绿色认证尽调数据
     */
    getGreenCertificationDDData: builder.query<
      {
        success: boolean;
        data: GreenCertificationDDData;
      },
      string
    >({
      query: projectId => `/professional-dd/${projectId}/green-cert`,
      providesTags: (result, error, projectId) => [
        { type: 'DDReport', id: `green-cert-${projectId}` },
      ],
    }),

    // ============================================================================
    // ESG尽职调查端点
    // ============================================================================

    /**
     * 保存ESG尽调草稿
     */
    saveESGDDDraft: builder.mutation<SaveDraftResponse, SaveESGDDRequest>({
      query: ({ projectId, data }) => ({
        url: `/professional-dd/${projectId}/esg/draft`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'DDProgress', id: projectId },
      ],
    }),

    /**
     * 提交ESG尽调报告
     */
    submitESGDDReport: builder.mutation<
      SubmitReportResponse,
      SubmitESGDDRequest
    >({
      query: ({ projectId, data }) => ({
        url: `/professional-dd/${projectId}/esg/submit`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'DDReport', id: projectId },
        { type: 'DDProgress', id: projectId },
      ],
    }),

    /**
     * 获取ESG尽调数据
     */
    getESGDDData: builder.query<
      {
        success: boolean;
        data: ESGDueDiligenceData;
      },
      string
    >({
      query: projectId => `/professional-dd/${projectId}/esg`,
      providesTags: (result, error, projectId) => [
        { type: 'DDReport', id: `esg-${projectId}` },
      ],
    }),

    // ============================================================================
    // 文档管理端点
    // ============================================================================

    /**
     * 上传尽调支持文件
     */
    uploadDDDocument: builder.mutation<UploadResponse, UploadDDDocumentRequest>(
      {
        query: ({ projectId, stepId, file }) => {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('stepId', stepId);
          return {
            url: `/professional-dd/${projectId}/documents`,
            method: 'POST',
            body: formData,
          };
        },
        invalidatesTags: (result, error, { projectId, stepId }) => [
          { type: 'DDDocument', id: projectId },
          { type: 'DDDocument', id: `${stepId}-${projectId}` },
        ],
      }
    ),

    /**
     * 获取尽调文档列表
     */
    getDDDocuments: builder.query<
      DDDocumentListResponse,
      GetDDDocumentsRequest
    >({
      query: ({ projectId, stepId }) => ({
        url: `/professional-dd/${projectId}/documents`,
        params: stepId ? { stepId } : {},
      }),
      providesTags: (result, error, { projectId, stepId }) => [
        { type: 'DDDocument', id: projectId },
        ...(stepId
          ? [{ type: 'DDDocument' as const, id: `${stepId}-${projectId}` }]
          : []),
      ],
    }),

    /**
     * 删除尽调文档
     */
    deleteDDDocument: builder.mutation<
      {
        success: boolean;
        message: string;
      },
      {
        projectId: string;
        documentId: string;
      }
    >({
      query: ({ projectId, documentId }) => ({
        url: `/professional-dd/${projectId}/documents/${documentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'DDDocument', id: projectId },
      ],
    }),

    // ============================================================================
    // 审核和质量控制端点
    // ============================================================================

    /**
     * 交易所审核尽调报告
     */
    reviewDDReport: builder.mutation<
      {
        success: boolean;
        message: string;
      },
      {
        projectId: string;
        stepId: string;
        reviewResult: 'approved' | 'rejected' | 'require_supplement';
        reviewComments: string;
        qualityRating?: number;
      }
    >({
      query: ({ projectId, stepId, ...data }) => ({
        url: `/professional-dd/${projectId}/${stepId}/review`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'DDReport', id: projectId },
        { type: 'DDProgress', id: projectId },
      ],
    }),

    /**
     * 获取尽调质量评价
     */
    getDDQualityAssessment: builder.query<
      {
        success: boolean;
        data: {
          legalQuality: number;
          financialQuality: number;
          greenCertQuality: number;
          esgQuality: number;
          overallQuality: number;
          qualityComments: string[];
        };
      },
      string
    >({
      query: projectId => `/professional-dd/${projectId}/quality`,
      providesTags: (result, error, projectId) => [
        { type: 'DDReport', id: `quality-${projectId}` },
      ],
    }),

    /**
     * 生成尽调综合报告
     */
    generateDDComprehensiveReport: builder.mutation<
      {
        success: boolean;
        data: {
          reportId: string;
          reportUrl: string;
        };
      },
      {
        projectId: string;
        includeAttachments?: boolean;
      }
    >({
      query: ({ projectId, includeAttachments = true }) => ({
        url: `/professional-dd/${projectId}/comprehensive-report`,
        method: 'POST',
        body: { includeAttachments },
      }),
    }),
  }),
});

// ============================================================================
// 导出Hooks
// ============================================================================

export const {
  // 通用查询
  useGetDDProjectDetailQuery,
  useGetDDProgressQuery,

  // 法律尽调
  useSaveLegalDDDraftMutation,
  useSubmitLegalDDReportMutation,
  useGetLegalDDDataQuery,

  // 财务尽调
  useSaveFinancialDDDraftMutation,
  useSubmitFinancialDDReportMutation,
  useGetFinancialDDDataQuery,

  // 绿色认证尽调
  useSaveGreenCertificationDDDraftMutation,
  useSubmitGreenCertificationDDReportMutation,
  useGetGreenCertificationDDDataQuery,

  // ESG尽调
  useSaveESGDDDraftMutation,
  useSubmitESGDDReportMutation,
  useGetESGDDDataQuery,

  // 文档管理
  useUploadDDDocumentMutation,
  useGetDDDocumentsQuery,
  useDeleteDDDocumentMutation,

  // 审核和质量控制
  useReviewDDReportMutation,
  useGetDDQualityAssessmentQuery,
  useGenerateDDComprehensiveReportMutation,
} = professionalDueDiligenceApi;
